import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { WidgetConfig } from "./types";
import { getStrings } from "./i18n";
import { ChatInterface } from "./ChatInterface";

interface Props {
  config: WidgetConfig;
}

function getIsMobile() {
  return typeof window !== "undefined" && window.innerWidth <= 520;
}

export function MrWineWidget({ config }: Props) {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(getIsMobile);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const t = getStrings(config.lang);

  // Track breakpoint changes
  useEffect(() => {
    const update = () => setMobile(window.innerWidth <= 520);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Escape key to close
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Keyboard push-up fix using visualViewport
  useEffect(() => {
    if (!open || !mobile) {
      setKeyboardOffset(0);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // gap = space taken by the keyboard (layout viewport minus visible viewport)
      const gap = window.innerHeight - vv.offsetTop - vv.height;
      setKeyboardOffset(Math.max(0, gap));
    };

    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      setKeyboardOffset(0);
    };
  }, [open, mobile]);

  const positionClass = `mw-${config.position}`;

  // Mobile: slide up from bottom. Desktop: scale + fade from corner.
  const panelVariants = mobile
    ? {
        initial: { y: "100%" },
        animate: { y: 0 },
        exit: { y: "100%" },
      }
    : {
        initial: { opacity: 0, y: 20, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 12, scale: 0.96 },
      };

  const panelTransition = mobile
    ? { duration: 0.32, ease: [0.32, 0.72, 0, 1] as [number, number, number, number] }
    : { duration: 0.25, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] };

  // On mobile, push the sheet up when keyboard is visible.
  // We use `bottom` as an inline style so it can transition via CSS
  // (the CSS rule adds `transition: bottom 0.22s ...`).
  const panelStyle =
    mobile && keyboardOffset > 0
      ? { bottom: keyboardOffset }
      : undefined;

  return (
    <div
      className={`mw-root mw-${config.theme} ${positionClass}`}
      style={{ ["--mw-accent" as string]: config.accentColor }}
      lang={config.lang}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            className="mw-panel"
            style={panelStyle}
            initial={panelVariants.initial}
            animate={panelVariants.animate}
            exit={panelVariants.exit}
            transition={panelTransition}
            role="dialog"
            aria-label={t.title}
            aria-modal="true"
          >
            {/* Handle bar — shown on mobile only via CSS */}
            <button
              type="button"
              className="mw-handle"
              onClick={() => setOpen(false)}
              aria-label={t.close}
            >
              <div className="mw-handle-bar" />
            </button>

            <div className="mw-header">
              <div className="mw-header-left">
                <span className="mw-dot" aria-hidden="true" />
                <span className="mw-title">{t.title}</span>
              </div>
              <button
                type="button"
                className="mw-close"
                aria-label={t.close}
                onClick={() => setOpen(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <ChatInterface config={config} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bubble — hidden on mobile while the sheet is open */}
      <button
        type="button"
        className={`mw-bubble${mobile && open ? " mw-hidden" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t.close : t.open}
        aria-expanded={open}
      >
        {open ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>
    </div>
  );
}
